import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import SiteHeader from "../components/Header";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import ImageMarquee from "../components/ImageMarquee";
import SEO from "../components/SEO";
import { accommodationOptions } from "../data/siteContent";

const gawkariRoomImages = [
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908943/WhatsApp_Image_2025-12-14_at_5.56.05_PM_zortcw.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908943/WhatsApp_Image_2025-12-14_at_5.55.59_PM_1_mzwcs0.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908942/WhatsApp_Image_2025-12-14_at_5.56.05_PM_1_jjold4.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908940/WhatsApp_Image_2025-12-14_at_5.56.03_PM_wadhse.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908938/WhatsApp_Image_2025-12-14_at_5.55.55_PM_p5o2hf.jpg",
  "https://res.cloudinary.com/dqlodrd4i/image/upload/v1765908936/WhatsApp_Image_2025-12-14_at_5.56.01_PM_1_yrtkiu.jpg",
];

const sectionVariants = {
  hidden: { opacity: 0, y: 45 },
  visible: { opacity: 1, y: 0 },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.12, duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function StayPage() {
  return (
    <>
      <SEO 
        title="Stay"
        description="Experience rustic luxury amidst the Sahyadris. Choose from traditional Wada rooms, hillside cottages, and family accommodations at Gawkari Hillside."
      />
      <SiteHeader />
      <main>
        <HeroSection />
        <AccommodationSection />
        <WhyChooseSection />
        <GallerySection />
      </main>
      <CTABanner />
      <Footer />
    </>
  );
}

function HeroSection() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % gawkariRoomImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.section 
      className="position-relative d-flex align-items-center" 
      initial={{ opacity: 0, y: 40 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      style={{
        backgroundImage: `url(${gawkariRoomImages[currentImageIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '80vh',
        transition: 'background-image 1s ease-in-out'
      }}
    >
      <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" style={{ zIndex: 1 }} />
      
      <div className="container position-relative" style={{ zIndex: 2 }}>
        <div className="text-center mx-auto text-white" style={{ maxWidth: '800px', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          <h1 className="display-3 fw-bold mb-4">Rustic luxury amidst the Sahyadris</h1>
          <p className="lead mb-5 opacity-75">
            Experience the perfect blend of traditional Maharashtrian architecture and modern comfort. Each accommodation tells a story of heritage while providing contemporary amenities for an unforgettable stay.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <a className="btn btn-primary btn-lg" href="#accommodations">
              View Accommodations
            </a>
            <Link className="btn btn-light btn-lg text-primary" to="/enquiry">
              Book Your Stay
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function AccommodationSection() {
  return (
    <motion.section id="accommodations" className="py-5 bg-light" variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2">Accommodation options</span>
          <h2 className="display-5 fw-bold text-dark">Choose your perfect hillside retreat</h2>
          <p className="text-secondary mx-auto mt-3" style={{ maxWidth: '640px' }}>Each space is thoughtfully designed to provide comfort while maintaining the authentic charm of rural Maharashtra.</p>
        </div>
        <div className="row row-cols-1 row-cols-md-3 g-4">
          {accommodationOptions.map((option, index) => (
            <div className="col" key={option.name}>
              <motion.article className="card h-100 border-0 shadow-sm" custom={index} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                <div className="card-body d-flex flex-column h-100 p-4">
                  <header className="mb-3">
                    <h3 className="h3 card-title text-primary">{option.name}</h3>
                  </header>
                  <p className="text-muted mb-4 flex-grow-1">{option.description}</p>
                  <ul className="list-unstyled mb-4">
                    {option.features.map((feature) => (
                      <li key={feature} className="d-flex align-items-center mb-2 text-secondary">
                        <span className="text-primary me-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto d-flex justify-content-between align-items-center">
                    <span className="badge bg-blue-lt">{option.idealFor}</span>
                    <Link className="btn btn-primary" to="/enquiry">
                      Book Now
                    </Link>
                  </div>
                </div>
              </motion.article>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

function WhyChooseSection() {
  return (
    <motion.section className="py-5 bg-white" variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2">Why choose us</span>
          <h2 className="display-5 fw-bold text-dark">More than just accommodation</h2>
        </div>
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
          <div className="col">
            <motion.div className="card h-100 p-4 text-center border-0 shadow-sm" custom={0} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              <div className="display-4 mb-3">🏠</div>
              <h3 className="h4 mb-3">Heritage & Comfort</h3>
              <p className="text-muted mb-0">Traditional architecture meets modern amenities for the perfect balance.</p>
            </motion.div>
          </div>
          <div className="col">
            <motion.div className="card h-100 p-4 text-center border-0 shadow-sm" custom={1} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              <div className="display-4 mb-3">🌿</div>
              <h3 className="h4 mb-3">Nature Immersion</h3>
              <p className="text-muted mb-0">Wake up to valley views and the sounds of the Sahyadri hills.</p>
            </motion.div>
          </div>
          <div className="col">
            <motion.div className="card h-100 p-4 text-center border-0 shadow-sm" custom={2} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              <div className="display-4 mb-3">👨‍👩‍👧‍👦</div>
              <h3 className="h4 mb-3">Family Friendly</h3>
              <p className="text-muted mb-0">Spaces designed for families to create lasting memories together.</p>
            </motion.div>
          </div>
          <div className="col">
            <motion.div className="card h-100 p-4 text-center border-0 shadow-sm" custom={3} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              <div className="display-4 mb-3">🍽️</div>
              <h3 className="h4 mb-3">Local Cuisine</h3>
              <p className="text-muted mb-0">Authentic Maharashtrian meals served fresh from our farm.</p>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function GallerySection() {
  return (
    <motion.section className="py-5 bg-light" variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
      <div className="container">
        <div className="text-center mb-5">
          <span className="badge bg-green-lt mb-2">Gallery</span>
          <h2 className="display-5 fw-bold text-dark">A glimpse of our accommodations</h2>
          <p className="text-secondary mx-auto mt-3" style={{ maxWidth: '640px' }}>See our actual rooms and spaces where you'll experience authentic Gawkari hospitality</p>
        </div>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
          {gawkariRoomImages.slice(0, 6).map((image, index) => (
            <div className="col" key={index}>
              <motion.div className="card h-100 border-0 shadow-sm overflow-hidden" custom={index} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
                <div className="ratio ratio-4x3">
                  <img src={image} className="object-fit-cover" alt={`Gawkari room ${index + 1} - Authentic accommodation at Hillside`} loading="lazy" />
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}
